import React, { useContext, useState } from 'react'
import { Trans, t } from '@lingui/macro'
import { Box, ButtonPrimary, Flex, Select } from 'front-end/src/components'
import Table, {
  TableBody,
  TableCol,
  TableRow
} from '../../../../components/common/table/Table'
import IconButton from '../../../../components/common/form/button/IconButton'
import IconBan from '../../../../components/common/icon/IconBan'
import AddCollaboratorsModal from './AddCollaboratorsModal'

import UserDetail from '../../../../components/common/user/user-detail/UserDetail'
import GeneralRemoveModal from '../../../../components/common/general-remove-modal/GeneralRemoveModal'
import { DashboardAccessRoleEnum } from 'front-end/src/graphql/private/PrivateSchemaTypes'
import {
  useGetDashboardCollaboratorsQuery,
  useDeleteCollaboratorMutation,
  useChangeCollaboratorRoleMutation
} from './DashboardCollaborators.codegen'
import { IconPlus } from 'front-end/src/components/common/icon/IconPlus'
import { ManageDashboardPageContext } from 'front-end/src/pages/dashboards-page/manage/ManageDashboardPage'

interface IProps {
  dashboardId: number
}

const ROLES = [
  {
    value: DashboardAccessRoleEnum.VIEWER,
    label: t`View only`
  },
  {
    value: DashboardAccessRoleEnum.EDITOR,
    label: t`Editor`
  }
]

const DashboardCollaborators = ({ dashboardId }: IProps) => {
  const { refetch } = useContext(ManageDashboardPageContext)
  const [showAddMembersModal, setShowAddMembersModal] = useState(false)
  const [showDeleteMemberModal, setShowDeleteMemberModal] = useState(false)

  const { data, refetch: refetchCollaborators } =
    useGetDashboardCollaboratorsQuery({
      variables: {
        id: dashboardId
      }
    })

  const [removeFn] = useDeleteCollaboratorMutation()
  const [changeRoleFn] = useChangeCollaboratorRoleMutation()

  return (
    <div className="MembersList">
      <Flex>
        <Box flexGrow={1}>
          <h2 className="MembersList__title">
            <Trans>Collaborators list</Trans>
          </h2>
          <p className="MembersList__hint">
            <Trans>
              The following people have individual access to this Dashboard
            </Trans>
          </p>
        </Box>
        <Box>
          <ButtonPrimary
            onClick={() => {
              setShowAddMembersModal(true)
            }}
            leftIcon={<IconPlus />}
            dataTest="AddCollaboratorButton"
          >
            <Trans>Add collaborator</Trans>
          </ButtonPrimary>
        </Box>
      </Flex>

      <Table className="TableMembers">
        <TableBody>
          {data?.admin?.dashboard?.collaborators?.map((collaborator) => {
            return collaborator &&
              collaborator.user &&
              !collaborator.user.isCurrentUser ? (
              <TableRow key={collaborator.user.id}>
                <TableCol className="MembersList__user">
                  <UserDetail
                    display_name={collaborator.user.display_name}
                    meta={collaborator.user.userIdentity}
                    imageUrl={collaborator.user.image_url}
                  />
                </TableCol>
                <TableCol collapsing>
                  <Select
                    fullWidth
                    options={ROLES}
                    value={ROLES.find(({ value }) => {
                      return value === collaborator.role?.toString()
                    })}
                    onChange={async (v) => {
                      await changeRoleFn({
                        variables: {
                          id: dashboardId,
                          collaboratorId: collaborator.id,
                          role: DashboardAccessRoleEnum[v?.value]
                        }
                      })
                      refetchCollaborators()
                      refetch()
                    }}
                    dataTest="collaboratorAccessSelect"
                  />
                </TableCol>
                <TableCol className="delete" collapsing>
                  <IconButton
                    icon={<IconBan />}
                    hint={t`Remove`}
                    onClick={() => setShowDeleteMemberModal(true)}
                    dataTest="DeleteButton"
                  />
                  <GeneralRemoveModal
                    show={showDeleteMemberModal}
                    entityName={t`collaborator`}
                    confirmButton={t`Remove`}
                    header={t`Remove collaborator`}
                    operation={async () => {
                      await removeFn({
                        variables: {
                          id: dashboardId,
                          collaboratorId: collaborator.id
                        }
                      })
                      refetchCollaborators()
                      refetch()
                      setShowDeleteMemberModal(false)
                    }}
                  />
                </TableCol>
              </TableRow>
            ) : null
          })}
        </TableBody>
      </Table>

      <AddCollaboratorsModal
        show={showAddMembersModal}
        dashboardId={dashboardId}
        refetch={refetchCollaborators}
        onHide={() => {
          setShowAddMembersModal(false)
        }}
      />
    </div>
  )
}

export default DashboardCollaborators
